import { useEffect, memo, useState } from "react";
import PropTypes from "prop-types";

import { Box, Divider, Grid, Typography } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { ButtonV1, DateTimePicker, DropdownV1 } from "components";
import { ErrorMsgV1 } from "CustomStyledComponents";

import { pascalCase } from "utils";
import { useFetchData } from "hooks";

import pickUpLocationSchema from "../TestSchema";
import { JOMS_API_BASE_V1_URL } from "constants";
import { getAddress } from "../DataModel";

const PickUpDetailsModal = ({
  selectedPreData,
  onCloseModal,
  onSavePickUpDetails,
}) => {
  const [selectedAddress, setSelectedAddress] = useState(
    selectedPreData?.sellerAddressMetadata,
  );

  const {
    control,
    getValues,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    mode: "onTouch",
    resolver: yupResolver(pickUpLocationSchema),
  });

  const { data: sellerAddressList, refetch: fetchSellerAddress } = useFetchData(
    "seller-pickup-address",
    `${JOMS_API_BASE_V1_URL}/v1/inventory-data/seller-and-pickup-details/dropdown/${selectedPreData?.id}`,
  );

  useEffect(() => {
    fetchSellerAddress();
    // Set deliveryType as it's value is used in validation
    setValue("deliveryType", selectedPreData?.deliveryType);
  }, []);

  const handleConfirmModal = async () => {
    const payload = {
      pickupSlotFrom: getValues("pickupSlotFrom"),
      pickupSlotTo: getValues("pickupSlotTo"),
      sellerAddressMetadata: selectedAddress,
      predoId: selectedPreData?.id,
    };
    onSavePickUpDetails(payload);
  };

  const handleChangeAddress = (e) => {
    const selectedAddress = sellerAddressList?.data?.sellerAddresses?.find(
      (item) => item?.vendorLocationDisplayName === e?.target.value,
    );
    setSelectedAddress(selectedAddress);
  };

  return (
    <Box as="form" onSubmit={handleSubmit(handleConfirmModal)}>
      <Typography variant="h4">Pick Up Details</Typography>
      <Divider />
      <Grid container py={4}>
        <Grid xs={2.5}>
          <Typography fontWeight={600} mb={1}>
            Pick-up slot: From
          </Typography>
          <Controller
            control={control}
            defaultValue={selectedPreData?.pickupSlotFrom}
            name={`pickupSlotFrom`}
            render={({ field: { value, onChange } }) => (
              <DateTimePicker
                dateStyle={{ width: "90%" }}
                value={value}
                onChange={onChange}
                disablePast={true}
              />
            )}
          />
          <ErrorMsgV1 msg={errors?.pickupSlotFrom?.message} />
        </Grid>
        <Grid xs={2.5}>
          <Typography fontWeight={600} mb={1}>
            Pick-up slot: To
          </Typography>
          <Controller
            control={control}
            defaultValue={selectedPreData?.pickupSlotTo}
            name={`pickupSlotTo`}
            render={({ field: { value, onChange } }) => (
              <DateTimePicker
                dateStyle={{ width: "90%" }}
                value={value}
                onChange={onChange}
              />
            )}
          />
          <ErrorMsgV1 msg={errors?.pickupSlotTo?.message} />
        </Grid>
        <Grid xs={2}>
          <Typography fontWeight={600} mb={1}>
            Seller name
          </Typography>
          <Typography>
            {pascalCase(selectedPreData?.sellerDetails?.sellerName)}
          </Typography>
        </Grid>
        <Grid xs={2.5}>
          <Typography fontWeight={600} mb={1}>
            Pickup location
          </Typography>
          <Controller
            control={control}
            name={"pickUpLocation"}
            defaultValue={
              selectedPreData?.sellerAddressMetadata?.vendorLocationDisplayName
            }
            render={({ field: { value, onChange } }) => (
              <DropdownV1
                sx={{
                  width: "80%",
                }}
                itemList={sellerAddressList?.data?.sellerAddresses}
                valueKey={"vendorLocationDisplayName"}
                displayKey={"vendorLocationDisplayName"}
                value={pascalCase(value ?? "")}
                onChange={(e) => {
                  onChange(e);
                  handleChangeAddress(e);
                }}
              />
            )}
          />
          <ErrorMsgV1 msg={errors?.pickUpLocation?.message} />
        </Grid>
        <Grid xs={2.5}>
          <Typography fontWeight={600} mb={1}>
            Seller address
          </Typography>
          <Typography>{getAddress(selectedAddress)}</Typography>
        </Grid>
      </Grid>
      <Box
        display={"flex"}
        sx={{
          justifyContent: "flex-end",
          gap: 4,
          alignItems: "center",
        }}
      >
        <ButtonV1 title={"Cancel"} variant="outlined" onClick={onCloseModal} />
        <ButtonV1 title={"Save"} type="submit" />
      </Box>
    </Box>
  );
};

PickUpDetailsModal.propTypes = {
  onCloseModal: PropTypes.func,
  onSavePickUpDetails: PropTypes.func,
  selectedPreData: PropTypes.object,
};

export default memo(PickUpDetailsModal);
